import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import StatusBadge from '@/components/base/StatusBadge';
import { supabase } from '@/lib/supabase';
import { formatDate, getWeekday } from '@/lib/date';
import { downloadPublicFile } from '@/lib/fileDownload';
import { getStatusMeta, lessonStatusMap } from '@/lib/statusMap';
import type { LessonRow, GroupInfo } from './WeekCalendar';

interface LessonDetailModalProps {
  open: boolean;
  onClose: () => void;
  lesson: LessonRow | null;
  group: GroupInfo | undefined;
  onChanged: () => void;
}

export default function LessonDetailModal({
  open,
  onClose,
  lesson,
  group,
  onChanged,
}: LessonDetailModalProps) {
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState(lesson?.status ?? 'planlandi');
  const [doc, setDoc] = useState<{ title: string; file_url: string; level: string | null } | null>(null);

  useEffect(() => {
    if (open && lesson) {
      setNewStatus(lesson.status);
      setUpdateError(null);
      setDoc(null);
      supabase
        .from('zehrahoca_documents')
        .select('title, file_url, level')
        .eq('lesson_id', lesson.id)
        .not('file_url', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
        .then(({ data }) => setDoc((data as { title: string; file_url: string; level: string | null } | null) ?? null));
    }
  }, [open, lesson]);

  if (!lesson) return null;

  const status = getStatusMeta(lesson.status, lessonStatusMap);

  async function handleStatusChange() {
    if (!lesson || newStatus === lesson.status) return;
    setUpdating(true);
    setUpdateError(null);
    try {
      const { error } = await supabase
        .from('zehrahoca_lessons')
        .update({ status: newStatus })
        .eq('id', lesson.id);
      if (error) throw error;
      onChanged();
    } catch (err) {
      setUpdateError((err as Error).message || 'Durum güncellenirken bir hata oluştu.');
    } finally {
      setUpdating(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={lesson.title}
      subtitle={group ? `${group.name}${lesson.level ? ` · ${lesson.level}` : ''}` : 'Ders Detayı'}
    >
      <div className="space-y-5">
        <div className="flex items-center gap-2">
          <StatusBadge meta={status} />
          <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-800">
            {lesson.type === 'birebir' ? 'Birebir Ders' : 'Grup Dersi'}
          </span>
          {lesson.live_link && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
              <i className="ri-live-line" /> Canlı
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-white p-3.5">
            <p className="flex items-center gap-2 text-xs font-semibold text-foreground-500">
              <i className="ri-calendar-line" /> Tarih
            </p>
            <p className="mt-1 text-sm font-medium capitalize text-foreground-900">
              {lesson.date ? `${formatDate(lesson.date)} · ${getWeekday(lesson.date)}` : '—'}
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-white p-3.5">
            <p className="flex items-center gap-2 text-xs font-semibold text-foreground-500">
              <i className="ri-time-line" /> Saat
            </p>
            <p className="mt-1 text-sm font-medium text-foreground-900">
              {lesson.start_time ?? '--:--'} - {lesson.end_time ?? '--:--'}
            </p>
          </div>
        </div>

        {lesson.subject && (
          <div>
            <p className="text-xs font-semibold text-foreground-500">Konu</p>
            <p className="mt-1 text-sm text-foreground-800">{lesson.subject}</p>
          </div>
        )}

        {lesson.description && (
          <div>
            <p className="text-xs font-semibold text-foreground-500">Açıklama</p>
            <p className="mt-1 text-sm text-foreground-700">{lesson.description}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {lesson.has_record && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-800">
              <i className="ri-vidicon-line" /> Kayıt Var
            </span>
          )}
          {lesson.has_document && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-800">
              <i className="ri-file-pdf-2-line" /> Doküman Var
            </span>
          )}
          {lesson.has_assignment && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-800">
              <i className="ri-task-line" /> Ödev Atandı
            </span>
          )}
        </div>

        {doc && (
          <div className="rounded-lg border border-background-200 bg-white p-3.5">
            <p className="flex items-center gap-2 text-xs font-semibold text-foreground-500">
              <i className="ri-file-pdf-2-line text-red-500" /> Ders PDF'i
            </p>
            <p className="mt-1.5 text-sm font-semibold text-foreground-900">{doc.title}</p>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              {group && (
                <span className="inline-flex items-center gap-1">
                  <i className="ri-team-line" /> {group.name}
                </span>
              )}
              {(doc.level || lesson.level) && (
                <span className="inline-flex items-center gap-1">
                  <i className="ri-bar-chart-line" /> {doc.level || lesson.level}
                </span>
              )}
            </div>
            <div className="mt-3 flex gap-2">
              <a
                href={doc.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary-500 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-external-link-line text-base" /> PDF Aç
              </a>
              <button
                type="button"
                onClick={() => downloadPublicFile(doc.file_url, doc.title)}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-background-300 bg-white px-3.5 py-2 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-download-line text-base" /> PDF İndir
              </button>
            </div>
          </div>
        )}

        <div className="rounded-lg border border-background-200 bg-background-50 p-3.5">
          <p className="text-xs font-semibold text-foreground-500">Ders Durumunu Güncelle</p>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer sm:w-auto"
            >
              {Object.entries(lessonStatusMap).map(([value, meta]) => (
                <option key={value} value={value}>{meta.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleStatusChange}
              disabled={updating || newStatus === lesson.status}
              className="whitespace-nowrap rounded-lg bg-secondary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-secondary-600 disabled:opacity-60 cursor-pointer"
            >
              {updating ? 'Kaydediliyor...' : 'Durumu Kaydet'}
            </button>
          </div>
          {updateError && (
            <p className="mt-2 text-xs text-red-600">{updateError}</p>
          )}
        </div>

        {lesson.live_link && (
          <a
            href={lesson.live_link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-primary-600"
          >
            <i className="ri-vidicon-line text-base" />
            Canlı Derse Git
          </a>
        )}
      </div>
    </Modal>
  );
}